/**
 * Личный кабинет — ТОЧНАЯ КОПИЯ legacy htdocs/core/templates/main_lk.tpl.
 * Разметка, классы, тексты и порядок блоков повторены 1:1 (main.css):
 *   .counterslist   — Счетчики            (initconters   → GET /api/lk/counters)
 *   .maincontact    — Основной контакт    (initmainusers → GET /api/lk/contacts?main=true)
 *   .contactslist   — Доп. Контакты       (initusers     → GET /api/lk/contacts?main=false)
 *   .avtoplateslist — Номера автомобилей  (initavtoplatelist → GET /api/plates)
 *   .changepass     — Смена пароля        (isneedinfo + changepassword)
 *
 * Модалки legacy — НЕ Bootstrap: это свои div'ы в .modalsblock
 * (.addnewusermodal / .removemodal / .avtoplatemodal + *bk-подложка).
 * Здесь они же, но как React-компоненты: те же классы, jQuery не нужен.
 *
 * Маски jquery.maskedinput перенесены руками:
 *   телефон  "+7 (999) 999-99-99"
 *   автономер "~999~~" (~ = [А-Яа-я]) и регион "9?99" (1–3 цифры).
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { api } from '@/api/client'
import { RecallPassword } from '@/components/RecallPassword'
import type { Counter, Plate, SntUser } from '@/api/types'
import { isTrue } from '@/lib/format'
import { useApi } from '@/lib/useApi'
import { useSession } from '@/session/SessionProvider'

/** GET /api/lk/me — нужен только флаг legacy isneedinfo (телефон владельца не заполнен). */
interface LkMe {
  need_info: boolean
}

/** Привязанный чат мессенджера (GET /api/telegram/status, GET /api/max/status). */
interface MessengerChat {
  /** bigint строкой: в JS числа больше 2^53 теряют точность. */
  chat_id: string
  username: string
  first_name: string
  enabled: boolean
}

/** Подключён ли мессенджер и какие аккаунты привязаны. */
interface MessengerStatus {
  linked: boolean
  chats: MessengerChat[]
}

/**
 * ОДНОРАЗОВАЯ ссылка привязки (живёт 15 минут).
 * link === null — имя бота узнать не удалось (мессенджер недоступен); тогда в error лежит
 * причина, и показать её надо, а не молча рисовать мёртвую кнопку.
 */
interface MessengerLink {
  token: string
  link: string | null
  bot: string
  error: string
}

/** POST /api/lk/password — legacy-ответ changepassword. */
interface PasswordResult {
  status: boolean
  value?: string
}

type Modal =
  | { kind: 'none' }
  | { kind: 'addContact' }
  | { kind: 'editContact'; user: SntUser; notowner: boolean }
  | { kind: 'removeContact'; userid: number }
  | { kind: 'addPlate' }
  | { kind: 'editPlate'; plate: Plate }
  | { kind: 'removePlate'; plateid: number }
  | { kind: 'changePassword' }
  | { kind: 'needInfo' }

const BADTYPE_MSG =
  'В пароле должны присутствовать и цифры и буквы лат.алфавита, пароль должен быть от 8 до 20 символов'

export function LkPage() {
  const { user, logout } = useSession()
  const navigate = useNavigate()

  const counters = useApi<Counter[]>('/api/lk/counters')
  const maincontacts = useApi<SntUser[]>('/api/lk/contacts?main=true')
  const contacts = useApi<SntUser[]>('/api/lk/contacts?main=false')
  const plates = useApi<Plate[]>('/api/plates')
  const telegram = useApi<MessengerStatus>('/api/telegram/status')
  const max = useApi<MessengerStatus>('/api/max/status')

  const [modal, setModal] = useState<Modal>({ kind: 'none' })
  /** .changepassalertinform: «Новый пароль установлен.» на 5 секунд, затем /lk?exit. */
  const [passwordChanged, setPasswordChanged] = useState(false)

  const countersCl = useClCollapse()
  const maincontactCl = useClCollapse()
  const contactsCl = useClCollapse()
  const platesCl = useClCollapse()
  const telegramCl = useClCollapse()
  const maxCl = useClCollapse()
  const changepassCl = useClCollapse()

  // legacy: window.location.href = '/lk?exit' через 5 сек — выход из аккаунта
  useEffect(() => {
    if (!passwordChanged) return
    const timer = window.setTimeout(() => {
      void logout().then(() => navigate('/login'))
    }, 5000)
    return () => window.clearTimeout(timer)
  }, [passwordChanged, logout, navigate])

  const closeModal = () => setModal({ kind: 'none' })

  /** legacy .contactedit → AJAX /showuser, затем модалка редактирования. */
  const openEditContact = (userid: number, notowner: boolean) => {
    void api
      .get<SntUser>(`/api/lk/contacts/${userid}`)
      .then((data) => setModal({ kind: 'editContact', user: data, notowner }))
      .catch(() => undefined)
  }

  /** legacy .avtoplateedit → AJAX /avtoplateshow, затем модалка редактирования. */
  const openEditPlate = (plateid: number) => {
    void api
      .get<Plate>(`/api/plates/${plateid}`)
      .then((data) => setModal({ kind: 'editPlate', plate: data }))
      .catch(() => undefined)
  }

  /** legacy .changepasswordbtn → сначала /isneedinfo, потом либо форма, либо «Ошибка». */
  const openChangePassword = () => {
    void api
      .get<LkMe>('/api/lk/me')
      .then((me) => setModal(me.need_info ? { kind: 'needInfo' } : { kind: 'changePassword' }))
      .catch(() => undefined)
  }

  return (
    <>
      <div className="curareaid" data-areaid={user?.id} />
      <div className="modalsblock">
        {modal.kind === 'addContact' && (
          <AddContactModal
            onClose={closeModal}
            onSaved={() => {
              closeModal()
              contacts.reload()
            }}
          />
        )}
        {modal.kind === 'editContact' && (
          <EditContactModal
            user={modal.user}
            notowner={modal.notowner}
            onClose={closeModal}
            onSaved={() => {
              closeModal()
              contacts.reload()
              maincontacts.reload()
            }}
          />
        )}
        {modal.kind === 'removeContact' && (
          <RemoveContactModal
            userid={modal.userid}
            onClose={closeModal}
            onRemoved={() => {
              closeModal()
              contacts.reload()
            }}
          />
        )}
        {modal.kind === 'addPlate' && (
          <PlateModal
            mode="add"
            onClose={closeModal}
            onSaved={() => {
              closeModal()
              plates.reload()
            }}
          />
        )}
        {modal.kind === 'editPlate' && (
          <PlateModal
            mode="edit"
            plate={modal.plate}
            onClose={closeModal}
            onSaved={() => {
              closeModal()
              plates.reload()
            }}
          />
        )}
        {modal.kind === 'removePlate' && (
          <RemovePlateModal
            plateid={modal.plateid}
            onClose={closeModal}
            onRemoved={() => {
              closeModal()
              plates.reload()
            }}
          />
        )}
        {modal.kind === 'changePassword' && (
          <ChangePasswordModal
            onClose={closeModal}
            onChanged={() => {
              closeModal()
              setPasswordChanged(true)
            }}
          />
        )}
        {modal.kind === 'needInfo' && <NeedInfoModal onClose={closeModal} />}
      </div>

      <div className="row" style={{ paddingTop: '10px' }}>
        <div className="counterslist col-xs-12 col-md-8 col-md-offset-2">
          <div className="clhead" style={countersCl.headStyle}>
            <div
              className="clhtitle clcollapse"
              data-collapsed={countersCl.collapsed ? 'true' : 'false'}
              onClick={countersCl.toggle}
            >
              Счетчики{' '}
              <span className="collapsespan">
                <i className={countersCl.arrow} />
              </span>
            </div>
          </div>
          <div className="clbody" style={countersCl.bodyStyle}>
            <table className="clbcounterstable">
              <thead>
                <tr className="clbcontactitemth">
                  <th className="counternbm">№</th>
                  <th className="countername">Номер счетчика</th>
                </tr>
              </thead>
              <tbody className="bodycounterstable">
                {counters.loading ? (
                  <tr>
                    <td style={{ textAlign: 'center' }} colSpan={2}>
                      Загрузка данных...
                    </td>
                  </tr>
                ) : (
                  (counters.data ?? []).map((counter, index) => (
                    <tr className="clbcontactitem" key={counter.id}>
                      <td className="counternbm">{index + 1}</td>
                      <td className="countername">{counter.number}</td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot className="footcounterstable" />
            </table>
          </div>
        </div>

        <div className="maincontact col-xs-12 col-md-8 col-md-offset-2">
          <div className="clhead" style={maincontactCl.headStyle}>
            <div
              className="clhtitle clcollapse"
              data-collapsed={maincontactCl.collapsed ? 'true' : 'false'}
              onClick={maincontactCl.toggle}
            >
              Основной контакт{' '}
              <span className="collapsespan">
                <i className={maincontactCl.arrow} />
              </span>
            </div>
            <div className="clhcounteradd" />
          </div>
          <div className="clbody" style={maincontactCl.bodyStyle}>
            <table className="clbcontactsеtable">
              <thead>
                <tr className="clbcontactitemth">
                  <th className="contactnbm">№</th>
                  <th className="contactname">ФИО</th>
                  <th className="contacttel">Телефон</th>
                  <th className="contactemail">Email</th>
                  <th className="contactcontrole">Управление</th>
                </tr>
              </thead>
              <tbody className="bodymaincontactsеtable">
                {maincontacts.loading ? (
                  <tr>
                    <td style={{ textAlign: 'center' }} colSpan={3}>
                      Загрузка данных...
                    </td>
                  </tr>
                ) : maincontacts.data === null ? (
                  <tr className="clbcontactitem">
                    <td colSpan={3} align="center">
                      Контакты отсутствуют.
                    </td>
                  </tr>
                ) : (
                  maincontacts.data.map((contact, index) => (
                    <tr className="clbcontactitem" key={contact.id}>
                      <td className="contactnbm">{index + 1}</td>
                      <td className="contactname">
                        {contact.familia} {contact.imuy} {contact.otchestvo}
                      </td>
                      <td className="contacttel">{contact.tel}</td>
                      <td className="contactemail">{contact.email}</td>
                      <td className="contactcontrole">
                        <div className="contactcontroleflex">
                          {/* data-notowner="false": владельцу в модалке доступна Email-рассылка */}
                          <div className="contactedit" onClick={() => openEditContact(contact.id, false)}>
                            <i className="fas fa-user-cog" />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot className="footmaincontactsеtable" />
            </table>
          </div>
        </div>

        <div className="contactslist col-xs-12 col-md-8 col-md-offset-2">
          <div className="clhead" style={contactsCl.headStyle}>
            <div
              className="clhtitle clcollapse"
              data-collapsed={contactsCl.collapsed ? 'true' : 'false'}
              onClick={contactsCl.toggle}
            >
              Доп. Контакты{' '}
              <span className="collapsespan">
                <i className={contactsCl.arrow} />
              </span>
            </div>
            <div className="clhcontactadd" onClick={() => setModal({ kind: 'addContact' })}>
              Добавить
            </div>
          </div>
          <div className="clbody" style={contactsCl.bodyStyle}>
            <table className="clbcontactsеtable">
              <thead>
                <tr className="clbcontactitemth">
                  <th className="contactnbm">№</th>
                  <th className="contactname">ФИО</th>
                  <th className="contacttel">Телефон</th>
                  <th className="contactemail">Email</th>
                  <th className="contactcontrole">Управление</th>
                </tr>
              </thead>
              <tbody className="bodycontactsеtable">
                {contacts.loading ? (
                  <tr>
                    <td style={{ textAlign: 'center' }} colSpan={3}>
                      Загрузка данных...
                    </td>
                  </tr>
                ) : contacts.data === null ? (
                  <tr className="clbcontactitem">
                    <td colSpan={3} align="center">
                      Контакты отсутствуют.
                    </td>
                  </tr>
                ) : (
                  contacts.data.map((contact, index) => (
                    <tr className="clbcontactitem" key={contact.id}>
                      <td className="contactnbm">{index + 1}</td>
                      <td className="contactname">
                        {contact.familia} {contact.imuy} {contact.otchestvo}
                      </td>
                      <td className="contacttel">{contact.tel}</td>
                      <td className="contactemail">{contact.email}</td>
                      <td className="contactcontrole">
                        <div className="contactcontroleflex">
                          {/* data-notowner="true": у доп. контакта чекбоксы рассылок не показываются */}
                          <div className="contactedit" onClick={() => openEditContact(contact.id, true)}>
                            <i className="fas fa-user-cog" />
                          </div>
                          <div
                            className="contactremove"
                            onClick={() => setModal({ kind: 'removeContact', userid: contact.id })}
                          >
                            <i className="fas fa-user-times" />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot className="footcontactsеtable" />
            </table>
          </div>
        </div>

        <div className="avtoplateslist col-xs-12 col-md-8 col-md-offset-2">
          <div className="clhead" style={platesCl.headStyle}>
            <div
              className="clhtitle clcollapse"
              data-collapsed={platesCl.collapsed ? 'true' : 'false'}
              onClick={platesCl.toggle}
            >
              Номера автомобилей{' '}
              <span className="collapsespan">
                <i className={platesCl.arrow} />
              </span>
            </div>
            <div className="clhavtoplateadd" onClick={() => setModal({ kind: 'addPlate' })}>
              Добавить
            </div>
          </div>
          <div className="clbody" style={platesCl.bodyStyle}>
            <table className="clbavtoplatestable">
              <thead>
                <tr className="clbavtoplateitemth">
                  <th className="avtoplatenbm">№</th>
                  <th className="avtoplatename">Номер</th>
                  <th className="avtoplatecontrol">Управление</th>
                </tr>
              </thead>
              <tbody className="bodyavtoplatestable">
                {plates.loading ? (
                  <tr>
                    <td style={{ textAlign: 'center' }} colSpan={3}>
                      Загрузка данных...
                    </td>
                  </tr>
                ) : plates.data === null ? (
                  <tr className="clbavtoplateitem">
                    <td colSpan={3} align="center">
                      Номера отсутствуют.
                    </td>
                  </tr>
                ) : (
                  plates.data.map((plate, index) => (
                    <tr className="clbavtoplateitem" key={plate.id}>
                      <td className="avtoplatenbm">{index + 1}</td>
                      <td className="avtoplatename">
                        {plate.avtoplatenumber} {plate.avtoplateregion}
                      </td>
                      <td className="avtoplatecontrole">
                        <div className="avtoplatecontroleflex">
                          <div className="avtoplateedit" onClick={() => openEditPlate(plate.id)}>
                            <i className="fas fa-user-cog" />
                          </div>
                          <div
                            className="avtoplateremove"
                            onClick={() => setModal({ kind: 'removePlate', plateid: plate.id })}
                          >
                            <i className="fas fa-user-times" />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot className="footavtoplatestable" />
            </table>
          </div>
        </div>

        {/*
          Telegram и MAX — блоки сверх legacy (ботов уведомлений жителям тогда не было).
          Своих CSS-классов не вводим (CLAUDE.md п.9), а весь LK-CSS в main.css привязан
          к классу БЛОКА (.changepass .clbody, .counterslist .clhead …) — новый класс
          остался бы вовсе без стилей. Поэтому переиспользуем .changepass: у него ровно
          та же форма — заголовок, тело, поясняющий текст и кнопка.
        */}
        <div className="changepass col-xs-12 col-md-8 col-md-offset-2">
          <div className="clhead" style={telegramCl.headStyle}>
            <div
              className="clhtitle clcollapse"
              data-collapsed={telegramCl.collapsed ? 'true' : 'false'}
              onClick={telegramCl.toggle}
            >
              Telegram{' '}
              <span className="collapsespan">
                <i className={telegramCl.arrow} />
              </span>
            </div>
            <div className="clhcounteradd" />
          </div>
          <div className="clbody" style={telegramCl.bodyStyle}>
            <MessengerBlock
              name="Telegram"
              base="/api/telegram"
              startLabel="Start"
              status={telegram.data}
              onChanged={telegram.reload}
            />
          </div>
        </div>

        <div className="changepass col-xs-12 col-md-8 col-md-offset-2">
          <div className="clhead" style={maxCl.headStyle}>
            <div
              className="clhtitle clcollapse"
              data-collapsed={maxCl.collapsed ? 'true' : 'false'}
              onClick={maxCl.toggle}
            >
              MAX{' '}
              <span className="collapsespan">
                <i className={maxCl.arrow} />
              </span>
            </div>
            <div className="clhcounteradd" />
          </div>
          <div className="clbody" style={maxCl.bodyStyle}>
            <MessengerBlock
              name="MAX"
              base="/api/max"
              startLabel="Начать"
              status={max.data}
              onChanged={max.reload}
            />
          </div>
        </div>

        <div className="changepass col-xs-12 col-md-8 col-md-offset-2">
          <div className="clhead" style={changepassCl.headStyle}>
            <div
              className="clhtitle clcollapse"
              data-collapsed={changepassCl.collapsed ? 'true' : 'false'}
              onClick={changepassCl.toggle}
            >
              Смена пароля{' '}
              <span className="collapsespan">
                <i className={changepassCl.arrow} />
              </span>
            </div>
            <div className="clhcounteradd" />
          </div>
          <div className="clbody" style={changepassCl.bodyStyle}>
            <div className="changepassalertinform">
              {passwordChanged && <div className="acceptmsg">Новый пароль установлен.</div>}
            </div>
            <div className="changepassinform">
              Для смены пароля, нажмите на кнопку ниже.
              <br />
              После смены пароля, вы будете разлогинены с аккаунта.
            </div>
            <div className="changepasswordbtn" onClick={openChangePassword}>
              Сменить пароль
            </div>
            {/* «Вспомнить пароль» — здесь же, сразу после кнопки смены пароля,
                тем же классом .changepasswordbtn (одинаковый вид/размер). */}
            <RecallPassword mode="profile" />
          </div>
        </div>
      </div>
    </>
  )
}

/**
 * Блок мессенджера (Telegram или MAX): подключить бота уведомлений или отключить его.
 *
 * ОДИН компонент на два канала, а не две копии: у них полностью совпадает и схема привязки,
 * и разметка — отличаются только название, адрес API и надпись на кнопке старта в самом
 * мессенджере. Две копии неизбежно разъехались бы при первой же правке (а правка здесь —
 * это работа с ключом от участка).
 *
 * Подписка — ЧАТ, а не галочка: житель открывает бота по ОДНОРАЗОВОЙ ссылке (15 минут)
 * и запускает его — это и есть согласие. Ссылку берём в момент нажатия, а не при загрузке
 * страницы: она короткоживущая и одноразовая, и выписывать её на каждый заход в ЛК —
 * значит плодить мёртвые токены (и держать в открытой вкладке ключ от участка).
 *
 * Один участок может подключить несколько аккаунтов (муж, жена) — поэтому список.
 * Классы только легаси (CLAUDE.md п.9): .changepassinform — текст, .changepasswordbtn —
 * кнопка, .acceptmsg — зелёное сообщение, .errorinfo — ошибка.
 */
function MessengerBlock({
  name,
  base,
  startLabel,
  status,
  onChanged,
}: {
  /** Как канал называется человеку: «Telegram», «MAX». */
  name: string
  /** Префикс API: '/api/telegram' или '/api/max'. */
  base: string
  /** Что житель нажмёт в самом мессенджере: «Start» в Telegram, «Начать» в MAX. */
  startLabel: string
  status: MessengerStatus | null
  onChanged: () => void
}) {
  const [error, setError] = useState('')
  const [waiting, setWaiting] = useState(false)

  const chats = status?.chats ?? []

  /** «Подключить» → берём свежую одноразовую ссылку и открываем бота. */
  const connect = (): void => {
    setError('')
    setWaiting(true)
    void api
      .get<MessengerLink>(`${base}/link`)
      .then((data) => {
        if (data.link === null) {
          setError(data.error !== '' ? data.error : 'Не удалось получить ссылку на бота.')
          return
        }
        // noopener — обязателен: без него открытая вкладка получает доступ к window.opener.
        window.open(data.link, '_blank', 'noopener')
      })
      .catch(() => setError('Не удалось получить ссылку на бота.'))
      .finally(() => setWaiting(false))
  }

  const disconnect = (chatId: string): void => {
    setError('')
    void api
      .post(`${base}/unlink`, { chat_id: chatId })
      .then(onChanged)
      .catch(() => setError(`Не удалось отключить ${name}.`))
  }

  if (status !== null && status.linked) {
    return (
      <>
        <div className="changepassalertinform">
          <div className="acceptmsg">{name} подключён.</div>
        </div>
        <div className="changepassinform">
          Счета, квитанции за электроэнергию, новости и голосования приходят в {name}.
        </div>
        {error !== '' && <div className="errorinfo">{error}</div>}

        {chats.map((chat) => (
          <div key={chat.chat_id}>
            <div className="changepassinform">
              Аккаунт:{' '}
              {chat.username !== '' ? `@${chat.username}` : chat.first_name !== '' ? chat.first_name : 'без имени'}
              {/* Бота заблокировали — уведомления не дойдут, и человек должен об этом знать. */}
              {!chat.enabled && ' — бот заблокирован, уведомления не приходят'}
            </div>
            <div className="changepasswordbtn" onClick={() => disconnect(chat.chat_id)}>
              Отключить
            </div>
          </div>
        ))}

        {/* Второй житель участка (жена, муж) подключает свой аккаунт отдельной ссылкой. */}
        <div className="changepassinform" style={{ marginTop: '10px' }}>
          Можно подключить ещё один аккаунт — например, второго члена семьи.
        </div>
        <div className="changepasswordbtn" onClick={connect}>
          {waiting ? 'Готовим ссылку...' : 'Подключить ещё'}
        </div>
      </>
    )
  }

  return (
    <>
      <div className="changepassinform">
        Подключите {name}, чтобы получать счета, квитанции за электроэнергию, новости и
        голосования.
        <br />
        Откроется бот — нажмите в нём «{startLabel}», и участок подключится сам.
      </div>
      {error !== '' && <div className="errorinfo">{error}</div>}
      <div className="changepasswordbtn" onClick={connect}>
        {waiting ? 'Готовим ссылку...' : `Подключить ${name}`}
      </div>
    </>
  )
}

/**
 * legacy-функция clcollapse(): при сворачивании у .clhead появляется нижняя граница,
 * .clbody скрывается. Квирк legacy сохранён: в исходном (развёрнутом) состоянии и в
 * свёрнутом рисуется стрелка «вниз», стрелка «вверх» появляется только после разворота.
 */
function useClCollapse(): {
  collapsed: boolean
  toggle: () => void
  headStyle: { borderBottom: string } | undefined
  bodyStyle: { display: string } | undefined
  arrow: string
} {
  const [collapsed, setCollapsed] = useState(false)
  const [toggled, setToggled] = useState(false)

  return {
    collapsed,
    toggle: () => {
      setCollapsed((value) => !value)
      setToggled(true)
    },
    headStyle: toggled ? { borderBottom: collapsed ? '1px solid #ccc' : '0px' } : undefined,
    bodyStyle: collapsed ? { display: 'none' } : undefined,
    arrow: toggled && !collapsed ? 'fas fa-angle-double-up' : 'fas fa-angle-double-down',
  }
}

/** Маска "+7 (999) 999-99-99" (jquery.maskedinput в legacy). */
function maskPhone(value: string): string {
  let digits = value.replace(/\D/g, '')
  if (digits.startsWith('7') || digits.startsWith('8')) digits = digits.slice(1)
  digits = digits.slice(0, 10)

  if (digits.length === 0) return ''
  let result = `+7 (${digits.slice(0, 3)}`
  if (digits.length >= 3) result += ')'
  if (digits.length > 3) result += ` ${digits.slice(3, 6)}`
  if (digits.length > 6) result += `-${digits.slice(6, 8)}`
  if (digits.length > 8) result += `-${digits.slice(8, 10)}`
  return result
}

/** Маска "~999~~", где $.mask.definitions['~'] = '[А-Яа-я]' — буква, 3 цифры, 2 буквы. */
function maskPlateNumber(value: string): string {
  const pattern = ['letter', 'digit', 'digit', 'digit', 'letter', 'letter'] as const
  let result = ''
  for (const char of value) {
    const kind = pattern[result.length]
    if (kind === undefined) break
    const ok = kind === 'letter' ? /[А-Яа-я]/.test(char) : /[0-9]/.test(char)
    if (ok) result += char
  }
  return result
}

/** Маска "9?99" — обязательная цифра плюс до двух необязательных. */
function maskPlateRegion(value: string): string {
  return value.replace(/\D/g, '').slice(0, 3)
}

/** legacy .clhcontactadd: в модалке добавления контакта только телефон (.addnewsmall). */
function AddContactModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [tel, setTel] = useState('')

  const submit = () => {
    void api.post('/api/lk/contacts', { adminputtel: tel }).then(onSaved).catch(() => undefined)
  }

  return (
    <>
      <div className="addnewusermodalbk" onClick={onClose} />
      <div className="addnewusermodal addnewsmall">
        <div className="admtitle">
          <div className="admname">Добавить контакт</div>
          <div className="admclose" onClick={onClose}>
            x
          </div>
        </div>
        <div className="admbody">
          <div className="admtel">
            <input
              type="text"
              className="adminputtel"
              placeholder="Телефон"
              style={{ width: '100%' }}
              value={tel}
              onChange={(event) => setTel(maskPhone(event.target.value))}
            />
          </div>
        </div>
        <div className="admfoot">
          <div className="admfaddbtnusertoarea" onClick={submit}>
            Добавить
          </div>
        </div>
      </div>
    </>
  )
}

/**
 * legacy .contactedit: ФИО + телефон + email; чекбокс Email рисуется только для основного
 * контакта (data-notowner="false"). Для доп. контакта его нет — как в legacy.
 * Чекбокс SMS убран: канал отключён (значение rassl_sms при сохранении не меняется).
 */
function EditContactModal({
  user,
  notowner,
  onClose,
  onSaved,
}: {
  user: SntUser
  notowner: boolean
  onClose: () => void
  onSaved: () => void
}) {
  const [familia, setFamilia] = useState(user.familia ?? '')
  const [imuy, setImuy] = useState(user.imuy ?? '')
  const [otchestvo, setOtchestvo] = useState(user.otchestvo ?? '')
  const [tel, setTel] = useState(maskPhone(user.tel ?? ''))
  const [email, setEmail] = useState(user.email ?? '')
  const [emailRassl, setEmailRassl] = useState(isTrue(user.rassl_email))

  const submit = () => {
    void api
      .put(`/api/lk/contacts/${user.id}`, {
        adminputfamilia: familia,
        adminputimuy: imuy,
        adminputotchestvo: otchestvo,
        adminputtel: tel,
        adminputemail: email,
        // SMS-канал отключён — тумблера в UI нет. Существующее значение rassl_sms
        // сохраняем как есть: колонку читает legacy-сайт на той же базе, гасить её нельзя.
        admcheckboxsms: !notowner && isTrue(user.rassl_sms) ? 'true' : 'false',
        admcheckboxemail: !notowner && emailRassl ? 'true' : 'false',
      })
      .then(onSaved)
      .catch(() => undefined)
  }

  return (
    <>
      <div className="addnewusermodalbk" onClick={onClose} />
      <div className="addnewusermodal">
        <div className="admtitle">
          <div className="admname">Редактировать контакт</div>
          <div className="admclose" onClick={onClose}>
            x
          </div>
        </div>
        <div className="admbody">
          <div className="admfio">
            <input
              type="text"
              className="adminputfamilia"
              placeholder="Фамилия"
              value={familia}
              onChange={(event) => setFamilia(event.target.value)}
            />
            <input
              type="text"
              className="adminputimuy"
              placeholder="Имя"
              value={imuy}
              onChange={(event) => setImuy(event.target.value)}
            />
            <input
              type="text"
              className="adminputotchestvo"
              placeholder="Отчество"
              value={otchestvo}
              onChange={(event) => setOtchestvo(event.target.value)}
            />
          </div>

          <div className="admtel">
            <input
              type="text"
              className="adminputtel"
              placeholder="Телефон"
              value={tel}
              onChange={(event) => setTel(maskPhone(event.target.value))}
            />
          </div>
          <div className="admemail">
            <input
              type="text"
              className="adminputemail"
              placeholder="Email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            {!notowner && (
              <label htmlFor="admcheckboxemail">
                <input
                  type="checkbox"
                  className="admcheckboxemail"
                  id="admcheckboxemail"
                  checked={emailRassl}
                  onChange={(event) => setEmailRassl(event.target.checked)}
                />
                Email
              </label>
            )}
          </div>
        </div>
        <div className="admfoot">
          <div className="admfeditbtnusertoarea" onClick={submit}>
            Сохранить
          </div>
        </div>
      </div>
    </>
  )
}

/** legacy .contactremove → .removemodal с кнопкой .delcontact. */
function RemoveContactModal({
  userid,
  onClose,
  onRemoved,
}: {
  userid: number
  onClose: () => void
  onRemoved: () => void
}) {
  const remove = () => {
    void api.delete(`/api/lk/contacts/${userid}`).then(onRemoved).catch(() => undefined)
  }

  return (
    <>
      <div className="removemodalbk" onClick={onClose} />
      <div className="removemodal">
        <div className="admtitle">
          <div className="admname">Подтверждение удаления</div>
          <div className="admclose" onClick={onClose}>
            x
          </div>
        </div>
        <div className="admbody">Подтвердить удаление контакта</div>
        <div className="admfoot">
          <div className="delcontact" onClick={remove}>
            Удалить
          </div>
        </div>
      </div>
    </>
  )
}

/** legacy .clhavtoplateadd / .avtoplateedit — одна и та же .avtoplatemodal. */
function PlateModal({
  mode,
  plate,
  onClose,
  onSaved,
}: {
  mode: 'add' | 'edit'
  plate?: Plate
  onClose: () => void
  onSaved: () => void
}) {
  const [number, setNumber] = useState(maskPlateNumber(plate?.avtoplatenumber ?? ''))
  const [region, setRegion] = useState(maskPlateRegion(plate?.avtoplateregion ?? ''))

  const submit = () => {
    const body = { platenumber: number, plateregion: region }
    const request =
      mode === 'add' && plate === undefined
        ? api.post('/api/plates', body)
        : api.put(`/api/plates/${plate?.id ?? 0}`, body)
    void request.then(onSaved).catch(() => undefined)
  }

  return (
    <>
      <div className="avtoplatemodalbk" onClick={onClose} />
      <div className="avtoplatemodal">
        <div className="admtitle">
          <div className="admname">
            {mode === 'add' ? 'Добавить номер автомобиля' : 'Редактировать номер автомобиля'}
          </div>
          <div className="admclose" onClick={onClose}>
            x
          </div>
        </div>
        <div className="admbody">
          <div className="avtoplatenumber">
            <input
              type="text"
              className="avtoplatenumberinput"
              value={number}
              onChange={(event) => setNumber(maskPlateNumber(event.target.value))}
            />
            <input
              type="text"
              className="avtoplateregioninput"
              value={region}
              onChange={(event) => setRegion(maskPlateRegion(event.target.value))}
            />
          </div>
        </div>
        <div className="admfoot">
          {mode === 'add' ? (
            <div className="addavtoplate" onClick={submit}>
              Добавить
            </div>
          ) : (
            <div className="editavtoplate" onClick={submit}>
              Сохранить
            </div>
          )}
        </div>
      </div>
    </>
  )
}

/** legacy .avtoplateremove → .removemodal с кнопкой .delavtoplate. */
function RemovePlateModal({
  plateid,
  onClose,
  onRemoved,
}: {
  plateid: number
  onClose: () => void
  onRemoved: () => void
}) {
  const remove = () => {
    void api.delete(`/api/plates/${plateid}`).then(onRemoved).catch(() => undefined)
  }

  return (
    <>
      <div className="removemodalbk" onClick={onClose} />
      <div className="removemodal">
        <div className="admtitle">
          <div className="admname">Подтверждение удаления</div>
          <div className="admclose" onClick={onClose}>
            x
          </div>
        </div>
        <div className="admbody">Подтвердить удаление номера автомобиля</div>
        <div className="admfoot">
          <div className="delavtoplate" onClick={remove}>
            Удалить
          </div>
        </div>
      </div>
    </>
  )
}

/** legacy .changepasswordbtn (телефон заполнен): пароль 8–20, цифра + строчная + заглавная латиница. */
function ChangePasswordModal({ onClose, onChanged }: { onClose: () => void; onChanged: () => void }) {
  const [password, setPassword] = useState('')
  const [errorinfo, setErrorinfo] = useState('')

  const submit = () => {
    void api
      .post<PasswordResult>('/api/lk/password', { newpassword: password })
      .then((result) => {
        if (result.status) {
          onChanged()
          return
        }
        if (result.value === 'badtype') setErrorinfo(BADTYPE_MSG)
      })
      .catch(() => undefined)
  }

  return (
    <>
      <div className="removemodalbk" onClick={onClose} />
      <div className="removemodal">
        <div className="admtitle">
          <div className="admname">Смена пароля</div>
          <div className="admclose" onClick={onClose}>
            x
          </div>
        </div>
        <div className="admbody">
          <div className="errorinfo">{errorinfo}</div>
          <div className="newpassword">
            <label>
              Пароль
              <input
                type="text"
                className="newpasswordinput"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </label>
          </div>
        </div>
        <div className="admfoot">
          <div className="changepasswordbtnaccept" onClick={submit}>
            Сменить
          </div>
        </div>
      </div>
    </>
  )
}

/** legacy: телефон основного контакта не заполнен (isneedinfo) — сменить пароль нельзя. */
function NeedInfoModal({ onClose }: { onClose: () => void }) {
  return (
    <>
      <div className="removemodalbk" onClick={onClose} />
      <div className="removemodal">
        <div className="admtitle">
          <div className="admname">Ошибка</div>
          <div className="admclose" onClick={onClose}>
            x
          </div>
        </div>
        <div className="admbody">Для смены пароля заполните номер телефона для основного контакта!</div>
      </div>
    </>
  )
}
